import React, { useState, useEffect } from "react";
import { Link, NavLink, useLocation } from "react-router-dom";
import { List, X, CaretDown } from "@phosphor-icons/react";
import logo from "@/assets/360view-logo.jpg";

const NAV = [
  {
    label: "Solutions",
    children: [
      { label: "Solar Surveillance Trailers", to: "/services/solar-surveillance-trailers" },
      { label: "Mobile Security Trailer Rentals", to: "/services/mobile-security-trailer-rentals" },
      { label: "Pole-Mounted Solar Surveillance", to: "/services/pole-mounted-solar-surveillance" },
      { label: "Temporary Site Surveillance", to: "/services/temporary-site-surveillance" },
      { label: "Remote Video Monitoring", to: "/services/remote-video-monitoring" },
      { label: "Live Video Monitoring", to: "/services/live-video-monitoring" },
      { label: "Rapid-Deployment Security", to: "/services/rapid-deployment-security" },
    ],
  },
  {
    label: "Systems",
    children: [
      { label: "Compare All Systems", to: "/systems/compare" },
      { label: "Mini Solar Pole System", to: "/systems/mini-solar-pole-system" },
      { label: "Standard 360° Trailer", to: "/systems/standard-surveillance-trailer" },
      { label: "Pro Advanced 360° Trailer", to: "/systems/pro-surveillance-trailer" },
    ],
  },
  {
    label: "Industries",
    children: [
      { label: "Construction Sites", to: "/industries/construction-site-security" },
      { label: "Commercial Properties", to: "/industries/commercial-property-security" },
      { label: "Auto Dealerships", to: "/industries/auto-dealership-security" },
      { label: "Equipment & Storage Yards", to: "/industries/equipment-yard-security" },
      { label: "Utilities & Energy", to: "/industries/utility-energy-security" },
      { label: "Government & Municipal", to: "/industries/government-municipal-security" },
      { label: "Parking Lots", to: "/industries/parking-lot-security" },
      { label: "Vacant Properties", to: "/industries/vacant-property-security" },
      { label: "Special Events", to: "/industries/special-event-security" },
      { label: "Critical Infrastructure", to: "/industries/critical-infrastructure-security" },
    ],
  },
  {
    label: "Service Areas",
    children: [
      { label: "Nevada", to: "/service-areas/nevada" },
      { label: "Utah", to: "/service-areas/utah" },
      { label: "Las Vegas", to: "/service-areas/nevada/las-vegas" },
      { label: "Henderson", to: "/service-areas/nevada/henderson" },
      { label: "Reno", to: "/service-areas/nevada/reno" },
    ],
  },
  {
    label: "Resources",
    children: [
      { label: "Learning Center", to: "/resources" },
      { label: "Case Studies", to: "/case-studies" },
      { label: "FAQ", to: "/faq" },
      { label: "About 360 View", to: "/about" },
    ],
  },
];

export default function Navbar() {
  const [scrolled, setScrolled] = useState(false);
  const [open, setOpen] = useState(false);
  const [activeMenu, setActiveMenu] = useState(null);
  const loc = useLocation();

  useEffect(() => { setOpen(false); setActiveMenu(null); }, [loc.pathname]);
  useEffect(() => {
    const on = () => setScrolled(window.scrollY > 20);
    window.addEventListener("scroll", on);
    return () => window.removeEventListener("scroll", on);
  }, []);

  return (
    <header
      data-testid="site-header"
      className={`fixed top-0 left-0 right-0 z-50 transition-colors duration-200 ${
        scrolled ? "bg-obsidian/90 backdrop-blur-xl border-b border-chrome" : "bg-obsidian/60 backdrop-blur-md border-b border-transparent"
      }`}
    >
      <div className="container-x flex items-center justify-between h-16 md:h-20">
        <Link to="/" data-testid="brand-logo" className="flex items-center gap-3 group">
          <div className="bg-white p-1 shadow-lg shadow-black/40 group-hover:shadow-solar/30 transition-shadow">
            <img src={logo} alt="360 View LLC logo" className="h-10 w-auto block" />
          </div>
          <div className="hidden sm:block leading-tight">
            <div className="eyebrow text-[10px] text-solar">Solar Surveillance</div>
            <div className="font-display font-black text-white text-xs tracking-widest">NEVADA · UTAH</div>
          </div>
        </Link>

        {/* Desktop nav */}
        <nav className="hidden lg:flex items-center gap-1" onMouseLeave={() => setActiveMenu(null)}>
          {NAV.map((item) => (
            <div
              key={item.label}
              className="relative"
              onMouseEnter={() => setActiveMenu(item.label)}
            >
              <button
                data-testid={`nav-${item.label.toLowerCase().replace(/\s/g, "-")}-btn`}
                className="px-3 py-2 flex items-center gap-1 text-sm font-semibold tracking-wide uppercase text-white/85 hover:text-solar transition-colors"
              >
                {item.label}
                <CaretDown size={12} weight="bold" />
              </button>
              {activeMenu === item.label && (
                <div className="absolute top-full left-0 min-w-[280px] bg-steel border border-chrome shadow-2xl">
                  {item.children.map((c) => (
                    <NavLink
                      key={c.to}
                      to={c.to}
                      data-testid={`nav-link-${c.to.replace(/\//g, "-")}`}
                      className="block px-5 py-3 text-sm text-white/85 border-b border-chrome last:border-b-0 hover:bg-obsidian hover:text-solar transition-colors"
                    >
                      {c.label}
                    </NavLink>
                  ))}
                </div>
              )}
            </div>
          ))}
        </nav>

        <div className="hidden lg:flex items-center gap-3">
          <a
            href="tel:7029290390"
            data-testid="header-call-btn"
            className="text-sm font-semibold text-white/85 hover:text-solar transition-colors"
          >
            702-929-0390
          </a>
          <Link
            to="/request-a-quote"
            data-testid="header-quote-btn"
            className="btn-solar"
          >
            Request Assessment
          </Link>
        </div>

        {/* Mobile trigger */}
        <button
          data-testid="mobile-menu-toggle"
          onClick={() => setOpen(!open)}
          className="lg:hidden w-10 h-10 flex items-center justify-center border border-chrome"
          aria-label="Toggle menu"
        >
          {open ? <X size={20} weight="bold" className="text-white" /> : <List size={20} weight="bold" className="text-white" />}
        </button>
      </div>

      {/* Mobile menu */}
      {open && (
        <div className="lg:hidden bg-obsidian border-t border-chrome max-h-[calc(100vh-4rem)] overflow-y-auto">
          <div className="p-4 space-y-1">
            {NAV.map((item) => (
              <details key={item.label} className="group border border-chrome">
                <summary
                  data-testid={`mobile-nav-${item.label.toLowerCase().replace(/\s/g, "-")}`}
                  className="px-4 py-3 text-sm font-bold uppercase tracking-wide text-white/90 cursor-pointer flex items-center justify-between"
                >
                  {item.label}
                  <CaretDown size={12} weight="bold" className="transition-transform group-open:rotate-180" />
                </summary>
                <div className="border-t border-chrome bg-steel">
                  {item.children.map((c) => (
                    <NavLink
                      key={c.to}
                      to={c.to}
                      className="block px-5 py-3 text-sm text-white/85 border-b border-chrome last:border-b-0 hover:text-solar"
                    >
                      {c.label}
                    </NavLink>
                  ))}
                </div>
              </details>
            ))}
            <div className="pt-3">
              <Link to="/request-a-quote" data-testid="mobile-quote-btn" className="btn-solar block text-center">Request Assessment</Link>
            </div>
          </div>
        </div>
      )}
    </header>
  );
}
